import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { MapOverlaysControl } from "components/MapOverlaysControl/MapOverlaysControl";
import { config } from "config";
import { fitBoundsToGeojson, setSourceData } from "helpers/mapHelpers";
import mapboxgl, { Map } from "mapbox-gl";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useStore } from "store/StoreConfigs";
import { globalViewMapStyles } from "./GlobalViewMapStyles";

export const GlobalViewMap = observer(function GlobalViewMap() {
    const classes = globalViewMapStyles();

    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<Map>();
    const [mapLoaded, setMapLoaded] = useState(false);

    const controlContainer = document.createElement("div");
    controlContainer.className = "mapboxgl-ctrl";
    const controlRef = useRef(controlContainer);

    const {
        globalViewStore: { lanesGeojson, loadingMap, pointsGeojson },
    } = useStore();

    useEffect(() => {
        if (map.current) {
            return;
        }

        map.current = new mapboxgl.Map({
            container: mapContainer.current!,
            ...config.map.initializationOptions,
        });

        map.current.on("load", () => {
            map.current?.addSource("laneLines", {
                type: "geojson",
                data: lanesGeojson,
            });

            map.current?.addSource("lanePoints", {
                type: "geojson",
                data: pointsGeojson,
            });

            map.current?.addSource("weather", {
                type: "raster",
                tiles: [
                    `https://maps.openweathermap.org/maps/2.0/weather/PA0/{z}/{x}/{y}?opacity=0.8&appid=${config.map.openWeatherMapKey}&palette=${config.map.accumulatedPrecipitationColorPalette}`,
                ],
                tileSize: 256,
            });

            map.current?.addLayer({
                id: "lanePoints",
                type: "circle",
                source: "lanePoints",
                paint: {
                    "circle-radius": 4,
                    "circle-color": ["string", ["get", "fillColor"]],
                    "circle-stroke-width": 2,
                    "circle-stroke-color": ["string", ["get", "strokeColor"]],
                },
            });

            map.current?.addLayer(
                {
                    id: "laneLines",
                    type: "line",
                    source: "laneLines",
                    layout: { "line-cap": "round" },
                    paint: {
                        "line-width": ["number", ["get", "width"]],
                        "line-color": ["string", ["get", "color"]],
                        "line-opacity": 1,
                    },
                },
                "lanePoints"
            );

            map.current?.addLayer(
                {
                    id: "weather",
                    type: "raster",
                    source: "weather",
                    layout: {
                        visibility: "none",
                    },
                },
                "laneLines"
            );

            map.current?.addControl({
                onAdd() {
                    return controlRef.current;
                },

                onRemove() {},
            });

            setMapLoaded(true);
        });

        return () => {
            map.current?.remove();
        };
    }, []);

    useEffect(() => {
        if (mapLoaded) {
            setSourceData(lanesGeojson, "laneLines", map.current);
            fitBoundsToGeojson(lanesGeojson, map.current);
        }
    }, [lanesGeojson, mapLoaded]);

    useEffect(() => {
        setSourceData(pointsGeojson, "lanePoints", map.current);
    }, [pointsGeojson, mapLoaded]);

    const overlays = [
        {
            name: "weather",
            displayName: "Weather",
        },
        {
            name: "portCongestion",
            displayName: "Port Congestion",
        },
        {
            name: "dwellTime",
            displayName: "Dwell Time",
        },
        {
            name: "routes",
            displayName: "Routes",
        },
    ];

    const [selectedOverlays, setSelectedOverlays] = useState<string[]>([]);

    useEffect(() => {
        overlays.forEach((overlay) => {
            if (map.current?.getLayer(overlay.name)) {
                map.current?.setLayoutProperty(
                    overlay.name,
                    "visibility",
                    selectedOverlays.includes(overlay.name) ? "visible" : "none"
                );
            }
        });
    }, [selectedOverlays]);

    return (
        <div ref={mapContainer} className={classes.container}>
            {loadingMap && (
                <div className={classes.indicatorContainer}>
                    <LoadingIndicator className={classes.indicator} isDimmed />
                </div>
            )}
            {ReactDOM.createPortal(
                <MapOverlaysControl
                    overlays={overlays}
                    selected={selectedOverlays}
                    onChange={(selected) => setSelectedOverlays(selected)}
                />,
                controlRef.current
            )}
        </div>
    );
});
